// Libraries
import { Box, ChakraProps, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useMemo } from 'react';
// Components
import { BreadcrumbItem } from './Components';
// Utils
import { CacheKeys } from '@utils/index';
import { vesselController } from '@controllers/index';
import { definitionList } from './Breadcrumb.definition';

/**
 * Represents the Breadcrumb component that displays a navigation trail.
 *
 * @component
 * @param {ChakraProps} props - The Chakra UI props for the Breadcrumb component.
 * @returns {ReactElement} The Breadcrumb component React element.
 */
export const Breadcrumb = (props: ChakraProps) => {
  const session = useSession();
  const clientId = session.data?.customer.id;
  const router = useRouter();
  const imo = Number(router.query.imo);
  const { data: vessels = [] } = useQuery({
    queryKey: [CacheKeys.VESSELS, clientId],
    queryFn: vesselController,
    refetchOnWindowFocus: false,
    useErrorBoundary: true,
    enabled: !!clientId,
  });

  /**
   * Get the name of the vessel associated with the current IMO.
   *
   * @function
   * @returns {string|undefined} The name of the vessel or undefined if not found.
   */
  const getVesselName = useCallback(() => {
    return vessels.find((vessel) => vessel.vesselImo === imo)?.vesselName;
  }, [imo, vessels]);

  const def = useMemo(() => definitionList.find((def) => router.route.startsWith(def.route)), [router.route]);

  return (
    <Box as='nav' color='grey.2' h='full' px={4} {...props}>
      <Flex as='ul' h='full' pt='1px' data-testid='breadcrumb-list'>
        <BreadcrumbItem
          title={def?.title}
          text={def?.text}
          href={def?.href}
          isCurrentPage={!def?.withVessel}
          withCaret={def?.withVessel}
        />
        {def?.withVessel && (
          <Fragment>
            <BreadcrumbItem isCurrentPage text={getVesselName()} />
          </Fragment>
        )}
      </Flex>
    </Box>
  );
};
